import { ContentPanel } from '@/components/content-panel';
import { Text } from '@mantine/core';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { FormikProps } from 'formik';
import { useCallback, useMemo, useRef } from 'react';
import { StandardButton } from '../../components/buttons/standard';
import { PrimaryLayout } from '../../layouts/primary';
import { ResetPasswordForm, ResetPasswordValues } from './form';
import { useSendResetPasswordLinkMutation } from './graphql.generated';

export function RequestPasswordResetPage() {
	const navigate = useNavigate();
	const { email } = useSearch({ from: '/request-password-reset' });
	const [sendResetPasswordLink, { data, loading, error }] = useSendResetPasswordLinkMutation();
	const getMessage = () => {
		if (loading) {
			return 'Sending email...';
		}

		if (error) {
			return 'An error occurred, please try again later';
		}

		if (data?.sendResetPasswordLink === true) {
			return 'An email has been sent with a link to reset your password';
		}

		// TODO: This allows leaking all the emails in the database
		if (data?.sendResetPasswordLink === false) {
			return 'An account with that email address does not exist';
		}

		return 'Enter you account email address and you’ll receive a link to reset your password';
	};
	const onSubmit = useCallback(
		async (values: ResetPasswordValues) => {
			const { data } = await sendResetPasswordLink({
				variables: { username: values.email.toLowerCase() },
			});

			if (data?.sendResetPasswordLink) {
				navigate({ to: '/password-link-sent', params: { email: values.email.toLowerCase() } });
			}
		},
		[navigate, sendResetPasswordLink]
	);

	const formRef = useRef<FormikProps<ResetPasswordValues>>(null);
	const triggerSubmit = useCallback(() => {
		formRef.current?.submitForm();
	}, [formRef]);

	const resetPasswordButton = useMemo(
		() => (
			<StandardButton variant="filled" onClick={triggerSubmit}>
				Reset Password
			</StandardButton>
		),
		[triggerSubmit]
	);

	return (
		<PrimaryLayout
			header={{ title: 'Reset Password', backTo: '/auth/login' }}
			footer={{ children: resetPasswordButton }}
		>
			<ContentPanel gap="md" justify="center" align="center" flex={1}>
				<Text size="sm" fw={400}>
					{getMessage()}
				</Text>
				<ResetPasswordForm onSubmit={onSubmit} formRef={formRef} email={email?.toLowerCase()} />
			</ContentPanel>
		</PrimaryLayout>
	);
}
